import { allowedThemes, widgetTheme } from './widgetDefaults'
import type { WidgetType } from './widgetDefaults'

export {
  WIDGET_TYPES,
  WIDGET_THEMES,
  IMAGE_THEMES,
  defaultWidgetOptions,
  allowedThemes,
  widgetTheme,
  TYPE_SCALE_PRESETS,
  withCopiedDesign
} from './widgetDefaults'
export type { WidgetType, WidgetTheme } from './widgetDefaults'

export type TWindowSize = 'desktop' | 'tablets' | 'phones'
type TWidgetOptionBase = {
  id: string
  name: string
  isCopyFromOtherWidgetEnabled: boolean
  requiredFeatures?: string[]
  helper?: string
}

export type TWidgetSelectOptionItem = {
  id: string
  name: string
  isDefault?: boolean
}
export type TWidgetSelectOptionBase = TWidgetOptionBase & {
  description: string
  items: TWidgetSelectOptionItem[]
}
export type TWidgetSelectOption = TWidgetSelectOptionBase & {
  type: 'select'
}
export type TWidgetModalSelectOption = TWidgetSelectOptionBase & {
  type: 'modalselect'
  imageFolder: string
}
export type TWidgetPaletteOptionItem = {
  id: string
  name: string
  default: string | number
}
export type TWidgetFontOption = TWidgetOptionBase & {
  type: 'font'
}
export type TWidgetPaletteOption = TWidgetOptionBase & {
  description: string
  type: 'palette'
  items: TWidgetPaletteOptionItem[]
}
export type TWidgetDisplayTargetsOptionItem = {
  id: TWindowSize
  name: string
  iconClass: string
  isImplementedInPersonalizer: boolean
  default: boolean
}
export type TWidgetDisplayTargetsOption = TWidgetOptionBase & {
  description: string
  type: 'displayTargets'
  items: TWidgetDisplayTargetsOptionItem[]
}
export type TWidgetToggleOption = TWidgetOptionBase & {
  description: string
  type: 'toggle'
  default: boolean
}
export type TWidgetGridOption = TWidgetOptionBase & {
  type: 'grid'
  items: TWidgetPaletteOptionItem[]
}
export type TWidgetCssOption = TWidgetOptionBase & {
  type: 'css'
}
export type TWidgetTypeDefinition = {
  id: WidgetType
  name: string
  options: (
    | TWidgetSelectOption
    | TWidgetModalSelectOption
    | TWidgetPaletteOption
    | TWidgetFontOption
    | TWidgetDisplayTargetsOption
    | TWidgetToggleOption
    | TWidgetGridOption
    | TWidgetCssOption
  )[]
  triggers?: TWidgetSelectOptionItem[]
  areas?: boolean
}
const commonPaletteOptionItems: TWidgetPaletteOptionItem[] = [
  {
    id: 'backgroundColor',
    name: 'Background',
    default: '#ffffff'
  },
  {
    id: 'textOnBackgroundColor',
    name: 'Text on background',
    default: '#4a4a4a'
  },
  {
    id: 'contrastColor',
    name: 'Contrast color',
    default: '#075985'
  },
  {
    id: 'textOnContrastColor',
    name: 'Text on contrast color',
    default: '#ffffff'
  },
  {
    id: 'borderColor',
    name: 'Border color',
    default: '#000000'
  }
]
const commonStructureItems = [
  {
    id: 'containerBorderRadius',
    name: 'Container Border Radius',
    default: '8px'
  },
  {
    id: 'containerBorderWidth',
    name: 'Container Border Width',
    default: '0px'
  },
  {
    id: 'buttonBorderRadius',
    name: 'Button Border Radius',
    default: '0px'
  },
  {
    id: 'answerButtonSpacing',
    name: 'Answer Button Spacing',
    default: '8px'
  },
  {
    id: 'formFieldSpacing',
    name: 'Form Field Spacing',
    default: '8px'
  }
]
const commonFontSizingItems: TWidgetPaletteOptionItem[] = [
  {
    id: 'headlineFontSize',
    name: 'Headline Font Size',
    default: '24px'
  },
  {
    id: 'descriptionFontSize',
    name: 'Description Font Size',
    default: '16px'
  },
  {
    id: 'answerButtonFontSize',
    name: 'Answer Button Font Size',
    default: '13px'
  },
  {
    id: 'formFieldFontSize',
    name: 'Form Field Font Size',
    default: '13px'
  },
  {
    id: 'submitButtonFontSize',
    name: 'Submit Button Font Size',
    default: '13px'
  },
  {
    id: 'lineHeight',
    name: 'Line Height',
    default: 1.5
  }
]
const commonDisplayTargetsOptionItems: TWidgetDisplayTargetsOptionItem[] = [
  {
    id: 'desktop',
    name: 'Desktops',
    iconClass: 'fa-desktop',
    isImplementedInPersonalizer: true,
    default: true
  },
  {
    id: 'tablets',
    name: 'Tablets',
    iconClass: 'fa-tablet',
    isImplementedInPersonalizer: true,
    default: true
  },
  {
    id: 'phones',
    name: 'Phones',
    iconClass: 'fa-mobile',
    isImplementedInPersonalizer: true,
    default: true
  }
]
const themeOption = (type: WidgetType): TWidgetModalSelectOption => ({
  id: 'theme',
  name: 'Theme',
  description: '',
  helper: '/docs/cta-funnels/widgets/#selecting-a-theme',
  requiredFeatures: ['right_bar_refinement'],
  type: 'modalselect',
  imageFolder: 'widget/theme',
  isCopyFromOtherWidgetEnabled: false,
  items: allowedThemes(type).map((id) => ({
    id,
    name: widgetTheme(id).name,
    isDefault: id === 'plain'
  }))
})

export const widgetTypeDefinitions: TWidgetTypeDefinition[] = [
  {
    id: 'bar',
    name: 'Bar',
    options: [
      themeOption('bar'),
      {
        id: 'font',
        name: 'Font',
        type: 'font',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: false
      },
      {
        id: 'fontSize',
        name: 'Font Sizing',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonFontSizingItems
      },
      {
        id: 'structure',
        name: 'Structure',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonStructureItems
      },
      {
        id: 'palette',
        name: 'Color palette',
        description: 'Choose colors for presenting your bar.',
        type: 'palette',
        isCopyFromOtherWidgetEnabled: true,
        items: commonPaletteOptionItems
      },
      {
        id: 'displayTargets',
        name: 'Display targets',
        description: 'Choose on which devices your bar should appear.',
        type: 'displayTargets',
        isCopyFromOtherWidgetEnabled: false,
        items: commonDisplayTargetsOptionItems
      },
      {
        id: 'placement',
        name: 'Position',
        description: 'Choose where your bar should appear.',
        requiredFeatures: ['right_bar_refinement'],
        type: 'select',
        isCopyFromOtherWidgetEnabled: false,
        items: [
          {
            id: 'top',
            name: 'Top',
            isDefault: true
          },
          {
            id: 'bottom',
            name: 'Bottom'
          }
        ]
      }, // {
      //     id: 'isSticky',
      //     name: 'Stay on screen when visitor scrolls',
      //     description: 'Choose whether the bar should stay visible as the visitor scrolls the page.',
      //     type: 'toggle',
      //     isCopyFromOtherWidgetEnabled: false,
      //     default: true,
      // },
      {
        id: 'buttonAnimationTrigger',
        name: 'Animate offer submit button',
        description: 'Call attention to your offer by pulsing the submit button.',
        requiredFeatures: ['right_bar_refinement'],
        type: 'select',
        isCopyFromOtherWidgetEnabled: false,
        items: [
          {
            id: '',
            name: "Don't pulse the button"
          },
          {
            id: 'scroll_90pc',
            name: 'Pulse the button at 90% scroll'
          },
          {
            id: 'once',
            name: 'Pulse the button once, after 5 seconds'
          },
          {
            id: 'infinite',
            name: 'Pulse the button every 5 seconds'
          }
        ]
      },
      {
        id: 'customCss',
        name: 'Custom CSS',
        helper: '/docs/cta-funnels/customizing-widgets/',
        type: 'css',
        isCopyFromOtherWidgetEnabled: true
      }
    ],
    triggers: [
      {
        id: 'immediate',
        name: 'Immediately when visitor starts to scroll'
      },
      {
        id: 'scroll',
        name: 'Scroll percentage'
      },
      {
        id: 'time',
        name: 'Time on page'
      },
      {
        id: 'manual',
        name: 'Only when manually triggered'
      },
      {
        id: 'barstatic',
        name: 'Immediately, pushes page down, scrolls with page content'
      }
    ]
  },
  {
    id: 'toaster',
    name: 'Toaster',
    options: [
      themeOption('toaster'),
      {
        id: 'font',
        name: 'Font',
        type: 'font',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: false
      },
      {
        id: 'fontSize',
        name: 'Font Sizing',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonFontSizingItems
      },
      {
        id: 'structure',
        name: 'Structure',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonStructureItems
      },
      {
        id: 'palette',
        name: 'Color palette',
        description: 'Choose colors for presenting your toaster.',
        type: 'palette',
        isCopyFromOtherWidgetEnabled: true,
        items: commonPaletteOptionItems
      },
      {
        id: 'displayTargets',
        name: 'Display targets',
        description: 'Choose on which devices your toaster should appear.',
        type: 'displayTargets',
        isCopyFromOtherWidgetEnabled: false,
        items: commonDisplayTargetsOptionItems
      },
      {
        id: 'placement',
        name: 'Position',
        description: 'Choose where your toaster should appear.',
        type: 'select',
        isCopyFromOtherWidgetEnabled: false,
        items: [
          {
            id: 'left',
            name: 'Bottom left'
          },
          {
            id: 'right',
            name: 'Bottom right',
            isDefault: true
          }
        ]
      },
      {
        id: 'buttonAnimationTrigger',
        name: 'Animate offer submit button',
        description: 'Call attention to your offer by pulsing the submit button.',
        type: 'select',
        isCopyFromOtherWidgetEnabled: false,
        items: [
          {
            id: '',
            name: "Don't animate the button"
          },
          {
            id: 'scroll_90pc',
            name: 'Animate the button at 90% scroll'
          },
          {
            id: 'once',
            name: 'Animate the button once, after 5 seconds'
          },
          {
            id: 'infinite',
            name: 'Animate the button every 5 seconds'
          }
        ]
      },
      {
        id: 'customCss',
        name: 'Custom CSS',
        helper: '/docs/cta-funnels/customizing-widgets/',
        type: 'css',
        isCopyFromOtherWidgetEnabled: true
      }
    ],
    triggers: [
      {
        id: 'immediate',
        name: 'Immediately on page load'
      },
      {
        id: 'scroll',
        name: 'Scroll percentage'
      },
      {
        id: 'time',
        name: 'Time on page'
      },
      {
        id: 'manual',
        name: 'Only when manually triggered'
      }
    ]
  },
  {
    id: 'inline',
    name: 'Embedded',
    options: [
      themeOption('inline'),
      {
        id: 'font',
        name: 'Font',
        type: 'font',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: false
      },
      {
        id: 'fontSize',
        name: 'Font Sizing',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonFontSizingItems
      },
      {
        id: 'structure',
        name: 'Structure',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonStructureItems
      },
      {
        id: 'palette',
        name: 'Color palette',
        description: 'Choose colors for presenting your embedded widget.',
        type: 'palette',
        isCopyFromOtherWidgetEnabled: true,
        items: commonPaletteOptionItems
      },
      {
        id: 'customCss',
        name: 'Custom CSS',
        helper: '/docs/cta-funnels/customizing-widgets/',
        type: 'css',
        isCopyFromOtherWidgetEnabled: true
      } //   {
      //     id: 'displayTargets',
      //     name: 'Display targets',
      //     description: 'Choose on which devices your toaster should appear.',
      //     type: 'displayTargets',
      //     isCopyFromOtherWidgetEnabled: false,
      //     items: commonDisplayTargetsOptionItems
      //   }
      // {
      //     id: 'buttonAnimationTrigger',
      //     name: 'Animate offer submit button',
      //     description: 'Call attention to your offer by pulsing the submit button.',
      //     type: 'select',
      //     isCopyFromOtherWidgetEnabled: false,
      //     items: [
      //         {
      //             id: '',
      //             name: "Don't animate the button",
      //         },
      //         {
      //             id: 'scroll_90pc',
      //             name: 'Animate the button at 90% scroll',
      //         },
      //         {
      //             id: 'once',
      //             name: 'Animate the button once, after 5 seconds',
      //         },
      //         {
      //             id: 'infinite',
      //             name: 'Animate the button every 5 seconds',
      //         },
      //     ]
      // },
    ],
    areas: true
  },
  {
    id: 'takeover',
    name: 'Full-screen Takeover',
    options: [
      themeOption('takeover'),
      {
        id: 'font',
        name: 'Font',
        type: 'font',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: false
      },
      {
        id: 'fontSize',
        name: 'Font Sizing',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonFontSizingItems
      },
      {
        id: 'structure',
        name: 'Structure',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonStructureItems
      },
      {
        id: 'palette',
        name: 'Color palette',
        description: 'Choose colors for presenting your takeover widget.',
        type: 'palette',
        isCopyFromOtherWidgetEnabled: true,
        items: commonPaletteOptionItems
      },
      {
        id: 'displayTargets',
        name: 'Display targets',
        description: 'Choose on which devices your toaster should appear.',
        type: 'displayTargets',
        isCopyFromOtherWidgetEnabled: false,
        items: commonDisplayTargetsOptionItems
      },
      {
        id: 'dismissible',
        name: 'Dismissible',
        description: 'Whether visitors can close this widget',
        type: 'toggle',
        isCopyFromOtherWidgetEnabled: false,
        default: true
      },
      {
        id: 'customCss',
        name: 'Custom CSS',
        helper: '/docs/cta-funnels/customizing-widgets/',
        type: 'css',
        isCopyFromOtherWidgetEnabled: true
      }
    ],
    triggers: [
      {
        id: 'immediate',
        name: 'Immediately on page load'
      },
      {
        id: 'scroll',
        name: 'Scroll percentage'
      },
      {
        id: 'time',
        name: 'Time on page'
      },
      {
        id: 'exit',
        name: 'Exit intent (visitor goes to leave the page)'
      },
      {
        id: 'manual',
        name: 'Only when manually triggered'
      }
    ]
  },
  {
    id: 'modal',
    name: 'Popup',
    options: [
      themeOption('modal'),
      {
        id: 'font',
        name: 'Font',
        type: 'font',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: false
      },
      {
        id: 'fontSize',
        name: 'Font Sizing',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonFontSizingItems
      },
      {
        id: 'structure',
        name: 'Structure',
        type: 'grid',
        requiredFeatures: ['right_bar_refinement'],
        isCopyFromOtherWidgetEnabled: true,
        items: commonStructureItems
      },
      {
        id: 'palette',
        name: 'Color palette',
        description: 'Choose colors for presenting your modal widget.',
        type: 'palette',
        isCopyFromOtherWidgetEnabled: true,
        items: commonPaletteOptionItems
      },
      {
        id: 'displayTargets',
        name: 'Display targets',
        description: 'Choose on which devices your toaster should appear.',
        type: 'displayTargets',
        isCopyFromOtherWidgetEnabled: false,
        items: commonDisplayTargetsOptionItems
      },
      {
        id: 'customCss',
        name: 'Custom CSS',
        helper: '/docs/cta-funnels/customizing-widgets/',
        type: 'css',
        isCopyFromOtherWidgetEnabled: true
      }
    ],
    triggers: [
      {
        id: 'immediate',
        name: 'Immediately on page load'
      },
      {
        id: 'scroll',
        name: 'Scroll percentage'
      },
      {
        id: 'time',
        name: 'Time on page'
      },
      {
        id: 'exit',
        name: 'Exit intent (visitor goes to leave the page)'
      },
      {
        id: 'manual',
        name: 'Only when manually triggered'
      }
    ]
  }
]
export default widgetTypeDefinitions
